import { registerPlugin } from "@capacitor/core"
import { normalizeNestRoutes, type NestRoute } from "../nest/routes"

export interface SecureComputerDescriptor {
    kind: "remote"
    name: string
    nodeId: string
    routes: NestRoute[]
    addedAt?: string
}

export interface SecureComputerConnection extends SecureComputerDescriptor {
    token: string
}

export interface SecureRegistryState {
    activeNodeId: string
    computers: SecureComputerDescriptor[]
}

interface SecureRegistryPlugin {
    list(): Promise<unknown>
    getConnection(options: { nodeId?: string }): Promise<unknown>
    add(options: { name: string; nodeId: string; routes: NestRoute[]; token: string }): Promise<unknown>
    activate(options: { nodeId: string }): Promise<unknown>
    remove(options: { nodeId: string }): Promise<unknown>
}

const nativePlugin = registerPlugin<SecureRegistryPlugin>("WildLarkSecureRegistry")

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null
}

function parseDescriptor(value: unknown): SecureComputerDescriptor | null {
    if (!isRecord(value)) return null
    const name = nonEmptyString(value.name)
    const nodeId = nonEmptyString(value.nodeId)
    let routes: NestRoute[]
    try {
        routes = normalizeNestRoutes(value.routes)
    } catch {
        return null
    }
    const addedAt = nonEmptyString(value.addedAt) ?? undefined
    if (value.kind !== "remote" || !name || !nodeId) return null
    return { kind: "remote", name, nodeId, routes, ...(addedAt ? { addedAt } : {}) }
}

export function parseSecureRegistryState(value: unknown): SecureRegistryState {
    if (!isRecord(value) || !isRecord(value.state) || value.state.version !== 2 || !Array.isArray(value.state.computers)) {
        throw new Error("Secure computer registry returned an invalid state")
    }
    const computers = value.state.computers.map(parseDescriptor).filter((computer): computer is SecureComputerDescriptor => computer !== null)
    const requestedActiveNodeId = nonEmptyString(value.state.activeNodeId)
    const activeNodeId =
        requestedActiveNodeId && computers.some((computer) => computer.nodeId === requestedActiveNodeId) ? requestedActiveNodeId : (computers[0]?.nodeId ?? "")
    return { activeNodeId, computers }
}

export function parseSecureRegistryConnection(value: unknown): SecureComputerConnection {
    if (!isRecord(value)) throw new Error("Secure computer registry returned an invalid connection")
    const descriptor = parseDescriptor(value.computer)
    if (!descriptor || !isRecord(value.computer)) throw new Error("Secure computer registry returned an invalid connection")
    const token = nonEmptyString(value.computer.token)
    if (!token) throw new Error("Secure computer registry did not return a credential")
    return { ...descriptor, token }
}

export const secureComputerRegistry = {
    async list(): Promise<SecureRegistryState> {
        return parseSecureRegistryState(await nativePlugin.list())
    },
    async getConnection(nodeId?: string): Promise<SecureComputerConnection> {
        return parseSecureRegistryConnection(await nativePlugin.getConnection(nodeId ? { nodeId } : {}))
    },
    async add(input: { name: string; nodeId: string; routes: NestRoute[]; token: string }): Promise<SecureRegistryState> {
        return parseSecureRegistryState(await nativePlugin.add(input))
    },
    async activate(nodeId: string): Promise<SecureComputerConnection> {
        return parseSecureRegistryConnection(await nativePlugin.activate({ nodeId }))
    },
    async remove(nodeId: string): Promise<SecureRegistryState> {
        return parseSecureRegistryState(await nativePlugin.remove({ nodeId }))
    },
}
